/* ============================================================
   PDP Story Drawer (PDP Phase 2 — Step 01)

   Behavior is initialized PER .pdp-story-drawer instance only.
   There is no global functionality: every query is scoped to the
   section root, and the single document-level listener (ESC) only
   acts when this instance's drawer is open.
   ============================================================ */
(function () {
  "use strict";

  function initStoryDrawer(root) {
    if (root.dataset.pdpStoryInit === "true") return;
    root.dataset.pdpStoryInit = "true";

    var overlay = root.querySelector(".pdp-story-drawer__overlay");
    var drawer = root.querySelector(".pdp-story-drawer__drawer");
    var closeEls = root.querySelectorAll("[data-pdp-story-close]");
    var openEls = root.querySelectorAll("[data-pdp-story-open]");
    var toggleEl = root.querySelector("[data-pdp-story-toggle]");
    var intro = root.querySelector(".pdp-story-drawer__intro");
    var tabs = root.querySelectorAll(".pdp-story-drawer__tab");
    var panels = root.querySelectorAll(".pdp-story-drawer__panel");

    if (!drawer || !overlay) return;

    function setActiveTab(name) {
      tabs.forEach(function (tab) {
        var isActive = tab.getAttribute("data-pdp-story-tab") === name;
        tab.classList.toggle("pdp-story-drawer__tab--active", isActive);
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
      });
      panels.forEach(function (panel) {
        var isActive = panel.getAttribute("data-pdp-story-panel") === name;
        if (isActive) {
          panel.removeAttribute("hidden");
        } else {
          panel.setAttribute("hidden", "");
        }
      });
    }

    function setActiveLink(name) {
      openEls.forEach(function (el) {
        el.classList.toggle(
          "pdp-story-drawer__link--active",
          el.getAttribute("data-pdp-story-open") === name
        );
      });
    }

    function openDrawer(name) {
      var tab = name || "mood";
      setActiveTab(tab);
      setActiveLink(tab);
      overlay.classList.add("is-open");
      drawer.classList.add("is-open");
      drawer.setAttribute("aria-hidden", "false");
    }

    function closeDrawer() {
      overlay.classList.remove("is-open");
      drawer.classList.remove("is-open");
      drawer.setAttribute("aria-hidden", "true");
    }

    function isOpen() {
      return drawer.classList.contains("is-open");
    }

    function setStoryExpanded(expanded) {
      if (!intro || !toggleEl) return;
      intro.classList.toggle("pdp-story-drawer__intro--expanded", expanded);
      toggleEl.setAttribute("aria-expanded", expanded ? "true" : "false");
      toggleEl.textContent = expanded ? "See less" : "See more";
    }

    // ---- "See more" / fade gating by rendered line count -------------------
    // The fade and the "See more" link only appear when the intro renders to
    // STORY_CLAMP_LINES or more. Line count is derived from actual rendered
    // height / line-height (not character count), so it tracks text wrapping
    // across screen sizes. JS toggles --clampable; the CSS keys the fade,
    // mobile max-height clamp, and the link's visibility off that class.
    var STORY_CLAMP_LINES = 3;

    function getIntroLineHeight() {
      var cs = window.getComputedStyle(intro);
      var lh = parseFloat(cs.lineHeight);
      if (isNaN(lh) || lh <= 0) {
        var fs = parseFloat(cs.fontSize) || 16;
        lh = fs * 1.4;
      }
      return lh;
    }

    function getIntroContentHeight() {
      // Sum each content child's full height. scrollHeight ignores any active
      // max-height clamp, so the measurement is the *natural* height regardless
      // of the current collapsed/expanded state. The toggle button is skipped.
      var total = 0;
      Array.prototype.forEach.call(intro.children, function (child) {
        if (child === toggleEl) return;
        total += child.scrollHeight;
      });
      return total;
    }

    function applyIntroClamp() {
      if (!intro || !toggleEl) return;
      // Direct rendered-height comparison (not line rounding): show the fade +
      // "See more" only when the content is *truly* >= STORY_CLAMP_LINES. The
      // 1px tolerance absorbs browser sub-pixel rounding so exactly-8-line
      // content is never hidden, while 7.x lines stay below the threshold.
      var lineHeight = getIntroLineHeight();
      var clampable =
        lineHeight > 0 &&
        getIntroContentHeight() >= STORY_CLAMP_LINES * lineHeight - 1;
      intro.classList.toggle("pdp-story-drawer__intro--clampable", clampable);
      if (!clampable) {
        // Short intro: ensure full content, no fade, no "See more" (CSS-driven).
        intro.classList.remove("pdp-story-drawer__intro--expanded");
        toggleEl.setAttribute("aria-expanded", "false");
        toggleEl.textContent = "See more";
      }
    }

    if (toggleEl) {
      toggleEl.addEventListener("click", function (event) {
        event.preventDefault();
        setStoryExpanded(
          toggleEl.getAttribute("aria-expanded") !== "true"
        );
      });
    }

    // Drawer triggers (Mood/Ingredients/Your Ritual links)
    openEls.forEach(function (el) {
      el.addEventListener("click", function (event) {
        event.preventDefault();
        openDrawer(el.getAttribute("data-pdp-story-open"));
      });
    });

    // Tab switching inside the drawer
    tabs.forEach(function (tab) {
      tab.addEventListener("click", function (event) {
        event.preventDefault();
        var name = tab.getAttribute("data-pdp-story-tab");
        setActiveTab(name);
        setActiveLink(name);
      });
    });

    // Close: close icon + overlay click
    closeEls.forEach(function (el) {
      el.addEventListener("click", function (event) {
        event.preventDefault();
        closeDrawer();
      });
    });

    // ESC closes — only acts for this instance when its drawer is open
    document.addEventListener("keydown", function (event) {
      if ((event.key === "Escape" || event.key === "Esc") && isOpen()) {
        closeDrawer();
      }
    });

    // Default tab state (drawer starts closed)
    setActiveTab("mood");
    setStoryExpanded(false);

    // Decide whether the intro is long enough to clamp/fade, now and on resize
    // (line wrapping — and the font-size/line-height breakpoint at 768px —
    // change the rendered line count). Re-measure once web fonts have loaded.
    applyIntroClamp();

    var clampRaf;
    window.addEventListener("resize", function () {
      if (clampRaf) cancelAnimationFrame(clampRaf);
      clampRaf = requestAnimationFrame(applyIntroClamp);
    });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(applyIntroClamp);
    }
  }

  function initAll(context) {
    var scope = context && context.querySelectorAll ? context : document;
    scope.querySelectorAll(".pdp-story-drawer").forEach(initStoryDrawer);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Re-init when the section is added/redrawn in the Theme Editor.
  document.addEventListener("shopify:section:load", function (event) {
    initAll(event.target);
  });
})();


// swt
document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('[data-pdp-story-toggle]').forEach(function (button) {
    const wrapper = button.previousElementSibling;

    if (!wrapper || !wrapper.classList.contains('pdp-story-text-wrapper')) return;

    // Hide button if text is <= 3 lines
    if (wrapper.scrollHeight <= wrapper.clientHeight + 2) {
      button.style.display = 'none';
      return;
    }

    button.addEventListener('click', function () {
      const expanded = wrapper.classList.contains('is-expanded');

      if (!expanded) {
        wrapper.style.maxHeight = wrapper.scrollHeight + 'px';
        wrapper.classList.add('is-expanded');

        button.textContent = 'See less';
        button.setAttribute('aria-expanded', 'true');
      } else {
        wrapper.style.maxHeight = wrapper.scrollHeight + 'px';

        requestAnimationFrame(() => {
          wrapper.style.maxHeight = 'calc(1.6em * 3)';
          wrapper.classList.remove('is-expanded');
        });

        button.textContent = 'See more';
        button.setAttribute('aria-expanded', 'false');
      }
    });
  });
});